import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const updateProductPriceSkill: Skill = {
  id: "updateProductPrice",
  description:
    "Cambia el precio de un producto existente. Útil cuando el usuario pida 'cambia el precio de X a Y' o 'sube/baja el precio de X'. Si no conoces el productId, usa searchProduct primero. Confirma con el usuario antes si tienes dudas.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      productId: {
        type: "string",
        description:
          "ID del producto. Si solo conoces el nombre, llama searchProduct primero.",
      },
      newPrice: {
        type: "number",
        description: "Nuevo precio (>= 0).",
      },
    },
    required: ["restaurantId", "productId", "newPrice"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    const newPrice = Number(args.newPrice);
    if (
      !restaurantId ||
      !productId ||
      !Number.isFinite(newPrice) ||
      newPrice < 0
    ) {
      return { ok: false, error: "Faltan datos válidos." };
    }

    const ok = await ctx.confirmation.confirm({
      title: "Cambiar precio",
      description: "Vas a actualizar el precio de este producto.",
      details: [
        { label: "Producto", value: productId },
        { label: "Nuevo precio", value: `$${newPrice.toLocaleString()}` },
      ],
      confirmLabel: "Actualizar",
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step(
        `Actualizando precio a $${newPrice.toLocaleString()}`,
        { durationMs: 700 },
      );
      await ctx.follow.clickAt(`[data-copilot-tab="productos"]`, {
        durationMs: 700,
        label: "Productos",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=productos`,
      );
      await sleep(900);
      await ctx.follow.step("Aplicando cambio…", { durationMs: 500 });
    }

    try {
      const { data } = await apiWithAuth.patch(
        `/restaurants/${restaurantId}/products/${productId}`,
        { price: newPrice },
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step(
          `✓ Precio actualizado: $${newPrice.toLocaleString()}`,
          { durationMs: 900, spinner: false },
        );
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "products",
        action: "updated",
        restaurantId,
        id: productId,
      });

      return {
        ok: true,
        result: {
          id: data?.id ?? productId,
          name: data?.name,
          price: data?.price ?? newPrice,
        },
        humanSummary: `Precio actualizado a $${newPrice}.`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step("✗ Error al actualizar precio", {
          durationMs: 700,
          spinner: false,
        });
        ctx.follow.clear();
      }
      return {
        ok: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Error actualizando precio.",
      };
    }
  },
};
